import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { FileText, X, Upload } from 'lucide-react';
import styles from '../Dashboard.module.css';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileSignature, faMoneyBillWave, faFileDownload, faCheckCircle, faTimesCircle, faPlus, faSearch, faShareAlt } from '@fortawesome/free-solid-svg-icons';

const Documents = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
    const [showUploadModal, setShowUploadModal] = useState(false);

    const docs = [
        { id: 101, title: 'Devis #2026-042 - Extension Terrasse', client: 'Mme. Dupont', date: '2026-02-14', status: 'signed', type: 'Devis', size: '2.4 MB' },
        { id: 102, title: 'Devis #2026-045 - Rénovation Bardage', client: 'M. Martin', date: '2026-02-15', status: 'sent', type: 'Devis', size: '1.8 MB' },
        { id: 103, title: 'Facture #F2026-12', client: 'SCI Les Pins', date: '2026-02-10', status: 'paid', type: 'Facture', size: '150 KB' },
        { id: 104, title: 'Contrat Maintenance Annuel', client: 'Mme. Lefebvre', date: '2026-02-08', status: 'signed', type: 'Contrat', size: '3.2 MB' },
        { id: 105, title: 'Plan Exécution - Villa Bellevue', client: 'Villa Bellevue', date: '2026-02-05', status: 'approved', type: 'Plan', size: '12 MB' },
        { id: 106, title: 'PV de Réception - Cuisine Martin', client: 'M. Martin', date: '2026-01-28', status: 'signed', type: 'PV', size: '4.5 MB' },
        { id: 107, title: 'Facture Acompte - Garage', client: 'M. Dubois', date: '2026-02-16', status: 'pending', type: 'Facture', size: '200 KB' },
    ];

    const filteredDocs = docs.filter(doc => {
        const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            doc.client.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = categoryFilter === 'all' || doc.type === categoryFilter;
        const matchesStatus = statusFilter === 'all' || doc.status === statusFilter;
        return matchesSearch && matchesCategory && matchesStatus;
    });

    const categories = ['Devis', 'Facture', 'Contrat', 'Plan', 'PV'];

    const getStatusStyle = (status) => {
        switch (status) {
            case 'signed': return { backgroundColor: '#dcfce7', color: '#166534', label: 'Signé', icon: faCheckCircle };
            case 'approved': return { backgroundColor: '#dbeafe', color: '#1e40af', label: 'Approuvé', icon: faCheckCircle };
            case 'sent': return { backgroundColor: '#fef9c3', color: '#854d0e', label: 'Envoyé', icon: faShareAlt };
            case 'paid': return { backgroundColor: '#dcfce7', color: '#15803d', label: 'Payé', icon: faMoneyBillWave };
            case 'pending': return { backgroundColor: '#fff7ed', color: '#9a3412', label: 'En attente', icon: faFileSignature };
            default: return { backgroundColor: '#f1f5f9', color: '#475569', label: 'Nouveau', icon: faFileDownload };
        }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'Devis': return faFileSignature;
            case 'Facture': return faMoneyBillWave;
            case 'Plan': return faFileDownload; // faMap?
            default: return faFileDownload;
        }
    };

    return (
        <div className={styles.dataSection}>
            <div className={styles.sectionHeader}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{ background: '#eff6ff', padding: '10px', borderRadius: '12px', color: '#3b82f6' }}>
                        <FontAwesomeIcon icon={faFileSignature} size="lg" />
                    </div>
                    <div>
                        <h3 style={{ margin: 0 }}>Gestion Documentaire</h3>
                        <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b' }}>Centralisez tous vos documents administratifs et techniques</p>
                    </div>
                </div>
                <button
                    onClick={() => setShowUploadModal(true)}
                    style={{
                        background: 'var(--color-primary)',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '10px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0 4px 6px -1px rgba(164, 198, 57, 0.4)'
                    }}
                >
                    <FontAwesomeIcon icon={faPlus} /> Nouveau Document
                </button>
            </div>

            <div className={styles.kpiRow} style={{ display: 'flex', gap: '20px', marginBottom: '30px' }}>
                <div className={styles.kpiCard} style={{ flex: 1, background: 'var(--color-surface, white)', padding: '20px', borderRadius: '16px', boxShadow: 'var(--shadow-md)', borderLeft: '4px solid #3b82f6', display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div style={{ background: 'rgba(59, 130, 246, 0.1)', width: '50px', height: '50px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6', fontSize: '1.5rem' }}>
                        <FontAwesomeIcon icon={faFileSignature} />
                    </div>
                    <div>
                        <div style={{ color: 'var(--color-text-light, #64748b)', fontSize: '0.9rem', fontWeight: '600' }}>Total Documents</div>
                        <div style={{ fontSize: '1.8rem', fontWeight: '800', color: 'var(--color-text, #1e293b)' }}>{docs.length}</div>
                    </div>
                </div>
                <div className={styles.kpiCard} style={{ flex: 1, background: 'var(--color-surface, white)', padding: '20px', borderRadius: '16px', boxShadow: 'var(--shadow-md)', borderLeft: '4px solid #10b981', display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div style={{ background: 'rgba(16, 185, 129, 0.1)', width: '50px', height: '50px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#10b981', fontSize: '1.5rem' }}>
                        <FontAwesomeIcon icon={faCheckCircle} />
                    </div>
                    <div>
                        <div style={{ color: 'var(--color-text-light, #64748b)', fontSize: '0.9rem', fontWeight: '600' }}>Signés / Validés</div>
                        <div style={{ fontSize: '1.8rem', fontWeight: '800', color: 'var(--color-text, #1e293b)' }}>{docs.filter(d => ['signed', 'approved', 'paid'].includes(d.status)).length}</div>
                    </div>
                </div>
                <div className={styles.kpiCard} style={{ flex: 1, background: 'var(--color-surface, white)', padding: '20px', borderRadius: '16px', boxShadow: 'var(--shadow-md)', borderLeft: '4px solid #f59e0b', display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <div style={{ background: 'rgba(245, 158, 11, 0.1)', width: '50px', height: '50px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f59e0b', fontSize: '1.5rem' }}>
                        <FontAwesomeIcon icon={faFileDownload} />
                    </div>
                    <div>
                        <div style={{ color: 'var(--color-text-light, #64748b)', fontSize: '0.9rem', fontWeight: '600' }}>En Attente</div>
                        <div style={{ fontSize: '1.8rem', fontWeight: '800', color: 'var(--color-text, #1e293b)' }}>{docs.filter(d => ['sent', 'pending'].includes(d.status)).length}</div>
                    </div>
                </div>
            </div>

            <div style={{
                marginBottom: '25px',
                display: 'flex',
                gap: '15px',
                flexWrap: 'wrap',
                background: 'var(--color-surface, white)',
                padding: '1.5rem',
                borderRadius: '16px',
                boxShadow: 'var(--shadow-sm)',
                alignItems: 'center'
            }}>
                <div style={{ flex: '1 1 250px', position: 'relative' }}>
                    <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-light, #94a3b8)' }} />
                    <input
                        type="text"
                        placeholder="Rechercher un document, client, référence..."
                        style={{
                            width: '100%',
                            padding: '12px 15px 12px 45px',
                            borderRadius: '10px',
                            border: '1px solid var(--color-light-gray, #e2e8f0)',
                            fontSize: '0.95rem',
                            background: 'var(--color-background, #f8fafc)',
                            color: 'var(--color-text)'
                        }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                <select
                    style={{ padding: '12px', borderRadius: '10px', border: '1px solid var(--color-light-gray, #e2e8f0)', minWidth: '160px', background: 'var(--color-background, #f8fafc)', fontWeight: '600', color: 'var(--color-text, #475569)' }}
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                >
                    <option value="all">Toutes catégories</option>
                    {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>

                <select
                    style={{ padding: '12px', borderRadius: '10px', border: '1px solid var(--color-light-gray, #e2e8f0)', minWidth: '160px', background: 'var(--color-background, #f8fafc)', fontWeight: '600', color: 'var(--color-text, #475569)' }}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                >
                    <option value="all">Tous status</option>
                    <option value="signed">Signé / Payé</option>
                    <option value="pending">En attente / Envoyé</option>
                </select>

                <div style={{ display: 'flex', background: 'var(--color-surface, #f1f5f9)', padding: '4px', borderRadius: '10px', marginLeft: 'auto' }}>
                    <button
                        onClick={() => setViewMode('list')}
                        style={{ padding: '8px 16px', border: 'none', borderRadius: '8px', background: viewMode === 'list' ? 'var(--color-background, white)' : 'transparent', color: viewMode === 'list' ? 'var(--color-primary)' : 'var(--color-text-light, #64748b)', fontWeight: '700', cursor: 'pointer', boxShadow: viewMode === 'list' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none' }}
                    >
                        Liste
                    </button>
                    <button
                        onClick={() => setViewMode('grid')}
                        style={{ padding: '8px 16px', border: 'none', borderRadius: '8px', background: viewMode === 'grid' ? 'var(--color-background, white)' : 'transparent', color: viewMode === 'grid' ? 'var(--color-primary)' : 'var(--color-text-light, #64748b)', fontWeight: '700', cursor: 'pointer', boxShadow: viewMode === 'grid' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none' }}
                    >
                        Grille
                    </button>
                </div>
            </div>

            {viewMode === 'list' ? (
                <div className={styles.tableContainer} style={{ background: 'var(--color-surface, white)', borderRadius: '16px', padding: '10px', boxShadow: 'var(--shadow-md)' }}>
                    <table className={styles.dataTable}>
                        <thead>
                            <tr>
                                <th>Type</th>
                                <th>Référence / Titre</th>
                                <th>Client / Projet</th>
                                <th>Date</th>
                                <th>Taille</th>
                                <th>Statut</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredDocs.map((doc) => {
                                const statusStyle = getStatusStyle(doc.status);
                                return (
                                    <tr key={doc.id}>
                                        <td>
                                            <span style={{
                                                fontSize: '0.7rem',
                                                fontWeight: '800',
                                                padding: '4px 10px',
                                                borderRadius: '6px',
                                                background: 'var(--color-background, #f1f5f9)',
                                                color: 'var(--color-text, #475569)',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px'
                                            }}>
                                                <FontAwesomeIcon icon={getTypeIcon(doc.type)} />
                                                {doc.type}
                                            </span>
                                        </td>
                                        <td><strong style={{ color: 'var(--color-text, #1e293b)' }}>{doc.title}</strong></td>
                                        <td>
                                            <div style={{ fontWeight: '600', color: 'var(--color-text, #475569)' }}>{doc.client}</div>
                                        </td>
                                        <td style={{ color: 'var(--color-text-light, #64748b)', fontSize: '0.9rem' }}>{new Date(doc.date).toLocaleDateString()}</td>
                                        <td style={{ color: 'var(--color-text-light, #94a3b8)', fontSize: '0.85rem' }}>{doc.size}</td>
                                        <td>
                                            <span style={{
                                                padding: '4px 12px',
                                                borderRadius: '20px',
                                                fontSize: '0.8rem',
                                                fontWeight: '700',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                background: statusStyle.backgroundColor,
                                                color: statusStyle.color
                                            }}>
                                                <FontAwesomeIcon icon={statusStyle.icon} />
                                                {statusStyle.label}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <button className={styles.iconBtn} title="Télécharger" style={{ width: '32px', height: '32px' }}><FontAwesomeIcon icon={faFileDownload} /></button>
                                                <button className={styles.iconBtn} title="Partager" style={{ width: '32px', height: '32px' }}><FontAwesomeIcon icon={faShareAlt} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                    {filteredDocs.map((doc) => {
                        const statusStyle = getStatusStyle(doc.status);
                        return (
                        <div key={doc.id} style={{
                                background: 'var(--color-surface, white)',
                                borderRadius: '16px',
                                padding: '20px',
                                boxShadow: 'var(--shadow-md)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '15px',
                                position: 'relative',
                                border: '1px solid transparent',
                                transition: 'all 0.2s'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <div style={{ background: 'var(--color-background, #f8fafc)', padding: '12px', borderRadius: '12px', color: 'var(--color-text-light, #64748b)' }}>
                                        <FontAwesomeIcon icon={getTypeIcon(doc.type)} size="lg" />
                                    </div>
                                    <span style={{
                                        padding: '4px 10px',
                                        borderRadius: '20px',
                                        fontSize: '0.75rem',
                                        fontWeight: '700',
                                        background: statusStyle.backgroundColor,
                                        color: statusStyle.color
                                    }}>
                                        {statusStyle.label}
                                    </span>
                                </div>

                                <div>
                                    <h4 style={{ margin: '0 0 5px 0', fontSize: '1rem', color: 'var(--color-text, #1e293b)' }}>{doc.title}</h4>
                                    <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--color-text-light, #64748b)' }}>{doc.client}</p>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto', paddingTop: '15px', borderTop: '1px solid var(--color-light-gray, #f1f5f9)' }}>
                                    <div style={{ fontSize: '0.8rem', color: 'var(--color-text-light, #94a3b8)' }}>
                                        {new Date(doc.date).toLocaleDateString()} • {doc.size}
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button className={styles.iconBtn} title="Télécharger" style={{ width: '32px', height: '32px', color: '#3b82f6', background: 'rgba(59, 130, 246, 0.1)' }}>
                                            <FontAwesomeIcon icon={faFileDownload} />
                                        </button>
                                        <button className={styles.iconBtn} title="Partager" style={{ width: '32px', height: '32px' }}>
                                            <FontAwesomeIcon icon={faShareAlt} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Simulated Upload Modal */}
            {showUploadModal && createPortal(
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 15 }}>
                    <div style={{ background: 'var(--color-surface)', width: '100%', maxWidth: 700, borderRadius: 24, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', overflow: 'hidden', maxHeight: '95vh', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
                            <div style={{ padding: '20px 30px', borderBottom: '1px solid var(--color-light-gray)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--color-light-background)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6' }}>
                                        <FileText size={20} />
                                    </div>
                                    <div>
                                        <h3 style={{ margin: 0, fontWeight: 900, color: 'var(--color-text)' }}>Ajouter un Document</h3>
                                        <p style={{ margin: 0, fontSize: 11, color: 'var(--color-text-light)', fontWeight: 600 }}>Nouveau Fichier / Document</p>
                                    </div>
                                </div>
                                <button type="button" onClick={() => setShowUploadModal(false)} style={{ background: 'var(--color-light-background)', border: 'none', cursor: 'pointer', color: 'var(--color-text-light)', width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <X size={20} />
                                </button>
                            </div>

                            <div style={{ padding: '25px 30px', display: 'flex', flexDirection: 'column', gap: 20, overflowY: 'auto', flex: 1 }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Type de document *</label>
                                    <select style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1.5px solid var(--color-light-gray)', outline: 'none', fontWeight: 700, background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 14 }}>
                                        {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                    </select>
                                </div>

                                <div>
                                    <label style={{ display: 'block', fontSize: 11, fontWeight: 900, color: 'var(--color-text-light)', textTransform: 'uppercase', marginBottom: 8 }}>Client / Projet</label>
                                    <input type="text" placeholder="Rechercher client..." style={{ width: '100%', padding: '12px', borderRadius: '12px', border: '1.5px solid var(--color-light-gray)', outline: 'none', fontWeight: 700, background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 14 }} />
                                </div>

                                <div style={{
                                    border: '2px dashed var(--color-light-gray)',
                                    borderRadius: '16px',
                                    padding: '35px 20px',
                                    textAlign: 'center',
                                    background: 'var(--color-light-background)',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 10
                                }}
                                    onMouseEnter={(e) => e.currentTarget.style.borderColor = '#3b82f6'}
                                    onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--color-light-gray)'}
                                >
                                    <div style={{ color: '#3b82f6', background: 'var(--color-surface)', width: 60, height: 60, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Upload size={28} />
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: '700', color: 'var(--color-text)', fontSize: 16 }}>Cliquez ou déposez un fichier ici</div>
                                        <div style={{ fontSize: '0.85rem', color: 'var(--color-text-light)', marginTop: '4px', fontWeight: 500 }}>PDF, JPG, PNG (Max 10MB)</div>
                                    </div>
                                </div>
                            </div>

                            <div style={{ padding: '20px 30px', background: 'var(--color-light-background)', borderTop: '1px solid var(--color-light-gray)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                                <div />
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <button type="button" onClick={() => setShowUploadModal(false)} style={{ padding: '10px 24px', borderRadius: 12, border: '1.5px solid var(--color-light-gray)', background: 'var(--color-surface)', color: 'var(--color-text)', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
                                        Annuler
                                    </button>
                                    <button onClick={() => { toast.success('Document ajouté avec succès !'); setShowUploadModal(false); }} style={{ padding: '10px 28px', borderRadius: 12, border: 'none', background: '#0f172a', color: '#fff', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, boxShadow: '0 10px 15px -3px rgba(15, 23, 42, 0.2)' }}>
                                        <Upload size={18} /> Uploader le document
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default Documents;
